import { useRef, useState } from "react";
import { toast } from "sonner";
import { Building2, X, Loader2, FileSpreadsheet, Upload, CheckSquare, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { api } from "../lib/api";
import { saveBlob } from "../lib/download";

export const BulkBranchBar = ({ selected, setSelected, filteredIds, branchOptions, onDone, children }) => {
  const [branch, setBranch] = useState("");
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);
  const target = branch === "__new" ? custom.trim() : branch;
  const n = selected.size;

  const apply = async () => {
    if (!target) { toast.warning("اختر الفرع"); return; }
    setBusy("apply");
    try {
      const r = await api.bulkSetBranch([...selected], target);
      toast.success(`تم نقل ${r.updated} موظف إلى فرع «${target}»`);
      setSelected(new Set()); setBranch(""); setCustom(""); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر النقل"); } finally { setBusy(""); }
  };
  const delAll = async () => {
    if (!window.confirm(`حذف ${n} موظف؟ سينتقلون إلى سلة المحذوفين ويمكن استرجاعهم خلال 30 يوماً.`)) return;
    if (window.prompt(`للتأكيد اكتب عدد الموظفين المحددين (${n}):`) !== String(n)) { toast.info("تم الإلغاء"); return; }
    setBusy("del");
    try { const r = await api.bulkDeleteEmployees([...selected]); toast.success(`تم نقل ${r.deleted} موظف إلى سلة المحذوفين`); setSelected(new Set()); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); } finally { setBusy(""); }
  };
  const tpl = async () => {
    setBusy("tpl");
    try { await saveBlob(await api.branchTemplate(), "فروع_الموظفين.xlsx"); } catch { toast.error("تعذّر التنزيل"); } finally { setBusy(""); }
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy("imp");
    try {
      const r = await api.importBranches(f);
      toast.success(`تم تحديث فرع ${r.updated} موظف`);
      if (r.unmatched.length) toast.warning(`${r.unmatched.length} سطر برقم هوية غير موجود: ${r.unmatched.slice(0, 5).map((u) => u.nid).join("، ")}`);
      onDone();
    } catch (er) { toast.error(er?.response?.data?.detail || "تعذّر الاستيراد"); } finally { setBusy(""); }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {n === 0 ? (
        <>
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" data-testid="bulk-branch-select-all" onClick={() => setSelected(new Set(filteredIds))}>
            <CheckSquare className="h-3.5 w-3.5" /> تحديد كل الظاهرين ({filteredIds.length})
          </Button>
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" data-testid="bulk-branch-template" disabled={!!busy} onClick={tpl}>
            {busy === "tpl" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5 text-blue-600" />} قالب الفروع Excel
          </Button>
          {children}
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs text-emerald-700 border-emerald-200" data-testid="bulk-branch-import" disabled={!!busy} onClick={() => fileRef.current?.click()}>
            {busy === "imp" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} استيراد الفروع
          </Button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onFile} data-testid="bulk-branch-import-input" />
        </>
      ) : (
        <div data-testid="bulk-branch-bar" className="w-full flex items-center gap-2 flex-wrap rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 animate-in fade-in slide-in-from-top-1 duration-200">
          <span className="text-sm font-semibold text-blue-800" data-testid="bulk-branch-count"><Building2 className="inline h-4 w-4 ml-1" />تم تحديد {n} موظف</span>
          {n < filteredIds.length && (
            <button className="text-xs text-blue-600 underline" data-testid="bulk-branch-select-all-inline" onClick={() => setSelected(new Set(filteredIds))}>تحديد الكل ({filteredIds.length})</button>
          )}
          <select data-testid="bulk-branch-select" value={branch} onChange={(e) => setBranch(e.target.value)} className="h-9 rounded-md border border-blue-200 bg-white px-2 text-sm min-w-40">
            <option value="">اختر الفرع</option>
            {branchOptions.filter((b) => b && b !== "غير محدد").map((b) => <option key={b} value={b}>{b}</option>)}
            <option value="__new">+ فرع جديد…</option>
          </select>
          {branch === "__new" && <Input data-testid="bulk-branch-new" className="h-9 w-40 bg-white" placeholder="اسم الفرع" value={custom} onChange={(e) => setCustom(e.target.value)} />}
          <Button size="sm" className="h-9 bg-blue-600 hover:bg-blue-700 gap-1.5" data-testid="bulk-branch-apply" disabled={!!busy} onClick={apply}>
            {busy === "apply" && <Loader2 className="h-4 w-4 animate-spin" />} نقل للفرع
          </Button>
          {children}
          <Button size="sm" variant="outline" className="h-9 gap-1.5 border-rose-300 bg-white text-rose-600 hover:bg-rose-50" data-testid="bulk-delete-employees" disabled={!!busy} onClick={delAll}>
            {busy === "del" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} حذف المحدد ({n})
          </Button>
          <button className="mr-auto text-slate-400 hover:text-slate-600" data-testid="bulk-branch-clear" onClick={() => setSelected(new Set())}><X className="h-4 w-4" /></button>
        </div>
      )}
    </div>
  );
};
